import { useCallback } from 'react';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import { postFunnelEvents } from 'features/funnel-events/actions';
import { InvestFunnelEvents } from 'features/funnel-events/types';
import { accountTypeChoiceEvents } from 'features/funnel-events/utils';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { blockInvestOnboardingOnWeb } from '../webGate';
import { ContinueOnboardingModal } from '../components/ContinueOnboardingModal';
import { discardPausedOnboardingState, resumeOnboardingState } from '../actions';
import { getReopenBlockedUntil, showReopenBlockedAlert } from '../utils/reopenBlock';
import useNavigateToNextScreen from './useNavigateToNextScreen';
// Starting onboarding only saves the account type, so anything else means the user answered at least one step.
const hasAnsweredAnyStep = ({ accountType: _accountType, params, ...answers }) => Object.keys(params ?? {}).length > 0 || Object.values(answers).some((answer) => answer !== undefined);
// Account type cards skip InvestSelectSecclAccountType, so the account type step and choice are sent here.
const useNavigateToIntroducingInvestmentAccount = () => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const navigation = useMainStackNavigation();
    const navigateToNextScreen = useNavigateToNextScreen();
    return useCallback((accountType) => {
        if (blockInvestOnboardingOnWeb())
            return;
        const reopenBlockedUntil = getReopenBlockedUntil(store.getState().invest.accounts, accountType);
        if (reopenBlockedUntil) {
            showReopenBlockedAlert(reopenBlockedUntil);
            return;
        }
        dispatch(postFunnelEvents([InvestFunnelEvents.ONB_INVEST_NEXT_ACCOUNT_TYPE, accountTypeChoiceEvents[accountType]]));
        const navigateToIntro = () => navigation.navigate('IntroducingInvestmentAccount', { accountType });
        const pausedOnboarding = store.getState().invest.pausedOnboarding?.[accountType];
        if (!pausedOnboarding || !hasAnsweredAnyStep(pausedOnboarding)) {
            if (pausedOnboarding) {
                dispatch(discardPausedOnboardingState(accountType));
            }
            navigateToIntro();
            return;
        }
        ContinueOnboardingModal.show({
            accountType,
            onContinue: () => {
                dispatch(resumeOnboardingState(accountType));
                dispatch(setDeviceSettingsKey('hasTriedSettingsUpInvest', true));
                navigateToNextScreen();
            },
            onStartAgain: () => {
                dispatch(discardPausedOnboardingState(accountType));
                navigateToIntro();
            },
        });
    }, [navigateToNextScreen]);
};
export default useNavigateToIntroducingInvestmentAccount;
